/** Unique key for each full-screen trowser; the value stored in `modalOpen`. */
export enum TrowserKey {
  Artifact = 'artifact',
  CreateEntityMap = 'createEntityMap',
  DocumentSummarySlides = 'documentSummarySlides',
  EditEntity = 'editEntity',
  EditEntityDiagram = 'editEntityDiagram',
  EditEntityMap = 'editEntityMap',
  EditEstateWaterfallDiagram = 'editEstateWaterfallDiagram',
  EditFamilyTree = 'editFamilyTree',
  EditMemo = 'editMemo',
  EducationPages = 'educationPages',
  EstateInsightsPreview = 'estateInsightsPreview',
  /** The single-profile editor. */
  GrowthProfile = 'growthProfile',
  /** The list of the household's growth profiles. */
  GrowthProfiles = 'growthProfiles',
  LinkHouseholdData = 'linkHouseholdData',
  PresentationDiagramEdit = 'presentationDiagramEdit',
  PresentationMode = 'presentationMode',
  ReviewSuggestions = 'reviewSuggestions',
  SmartReviewOnboarding = 'smartReviewOnboarding',
  ValidationQuestions = 'validationQuestions',
  /**
   * Opt-out for page shells that use the full-screen layout but aren't
   * dismissible trowsers (e.g. the presentation designer); they keep native
   * routing and skip the open-state / Back behavior.
   */
  Standalone = 'standalone',
}

/**
 * Maps each trowser key to its `modalData` shape. Trowsers augment this from
 * their own module (declaration merging) so this layer imports no feature types:
 *
 * ```ts
 * declare module '@/modules/navigation/trowsers/trowser.types' {
 *   interface TrowserDataMap {
 *     [TrowserKey.EditEntity]: { entityType: EntityType };
 *   }
 * }
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface -- augmented per trowser
export interface TrowserDataMap {}

/** A trowser's transient `modalData` (its map entry minus the URL-driven `section`). */
export type TrowserData<K extends TrowserKey> = K extends keyof TrowserDataMap
  ? Omit<TrowserDataMap[K], 'section'>
  : undefined;

/** A trowser's `section` type, from its map entry's `section` field (else `string`). */
export type TrowserSection<K extends TrowserKey> =
  K extends keyof TrowserDataMap
    ? TrowserDataMap[K] extends { section?: infer S extends string }
      ? S
      : string
    : string;

const URL_TROWSER_KEYS = new Set<string>(
  Object.values(TrowserKey).filter((key) => key !== TrowserKey.Standalone)
);

/** True for keys that participate in URL-driven open state (excludes Standalone). */
export function isTrowserKey(
  value: string | null | undefined
): value is TrowserKey {
  return !!value && URL_TROWSER_KEYS.has(value);
}

/** Query param holding the open-trowser stack. */
export const TROWSER_PARAM = 'modalOpen' as const;
/** Query param holding the topmost trowser's section. */
export const TROWSER_SECTION_PARAM = 'section' as const;

export interface OpenTrowser {
  key: TrowserKey;
  /** The instance being edited (e.g. an entity id), if not implied by the route. */
  target?: string;
}

/** Serializes the stack as comma-separated `key[:target]`, bottom→top. */
export function serializeStack(stack: OpenTrowser[]): string {
  return stack
    .map(({ key, target }) => (target ? `${key}:${target}` : key))
    .join(',');
}

export function parseStack(param: string | null): OpenTrowser[] {
  if (!param) return [];
  return param
    .split(',')
    .map((segment): OpenTrowser | null => {
      const [key, ...targetParts] = segment.split(':');
      if (!isTrowserKey(key)) return null;
      // Rejoin in case a target contains ':' — keys never do.
      const target = targetParts.join(':') || undefined;
      return { key, target };
    })
    .filter((entry): entry is OpenTrowser => entry !== null);
}
